import React, { useState, useEffect } from 'react';
import { Tag, tagsService } from '../services/tags.service';
import { FiX, FiPlus, FiTag } from 'react-icons/fi';
import toast from 'react-hot-toast';

interface TagSelectorProps {
  selectedTags: Tag[];
  onTagsChange: (tags: Tag[]) => void;
  onAddTag: (tagId: number) => Promise<void>;
  onRemoveTag: (tagId: number) => Promise<void>;
}

export const TagSelector: React.FC<TagSelectorProps> = ({
  selectedTags,
  onTagsChange,
  onAddTag,
  onRemoveTag
}) => {
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [showTagMenu, setShowTagMenu] = useState(false);
  const [showCreateTag, setShowCreateTag] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#6B7280');
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    loadTags();
  }, []);

  const loadTags = async () => {
    try {
      const tags = await tagsService.getTags();
      setAllTags(tags);
    } catch (error) {
      console.error('Failed to load tags:', error);
    }
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) {
      toast.error('Tag name is required');
      return;
    }

    setIsCreating(true);
    try {
      const newTag = await tagsService.createTag({
        name: newTagName.trim(),
        color: newTagColor
      });
      
      setAllTags([...allTags, newTag]);
      setNewTagName('');
      setShowCreateTag(false);
      toast.success('Tag created successfully');
      
      // Automatically add the new tag
      await handleAddTag(newTag);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to create tag');
    } finally {
      setIsCreating(false);
    }
  };

  const handleAddTag = async (tag: Tag) => {
    try {
      await onAddTag(tag.id);
      onTagsChange([...selectedTags, tag]);
      setShowTagMenu(false);
    } catch (error) {
      toast.error('Failed to add tag');
    }
  };

  const handleRemoveTag = async (tag: Tag) => {
    try {
      await onRemoveTag(tag.id);
      onTagsChange(selectedTags.filter(t => t.id !== tag.id));
    } catch (error) {
      toast.error('Failed to remove tag');
    }
  };

  const availableTags = allTags.filter(
    tag => !selectedTags.some(selected => selected.id === tag.id)
  );

  return (
    <div>
      <label className="block text-sm font-medium text-gray-400 mb-2">
        <FiTag className="inline-block w-4 h-4 mr-1" />
        Tags
      </label>
      
      {/* Selected Tags */}
      <div className="flex flex-wrap gap-2 mb-2">
        {selectedTags.map(tag => (
          <span
            key={tag.id}
            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
            style={{ backgroundColor: tag.color + '20', color: tag.color }}
          >
            {tag.name}
            <button
              onClick={() => handleRemoveTag(tag)}
              className="ml-1 hover:opacity-70"
            >
              <FiX className="w-3 h-3" />
            </button>
          </span>
        ))}
        
        {/* Add Tag Button */}
        <button
          onClick={() => setShowTagMenu(!showTagMenu)}
          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-700 text-gray-300 hover:bg-gray-600"
        >
          <FiPlus className="w-3 h-3 mr-1" />
          Add Tag
        </button>
      </div>

      {/* Tag Menu */}
      {showTagMenu && (
        <div className="relative">
          <div className="absolute z-10 mt-1 w-64 bg-gray-800 rounded-lg shadow-lg border border-gray-700">
            {/* Available Tags */}
            {availableTags.length > 0 && (
              <div className="p-2 border-b border-gray-700">
                <p className="text-xs text-gray-400 mb-2">Available Tags</p>
                <div className="space-y-1">
                  {availableTags.map(tag => (
                    <button
                      key={tag.id}
                      onClick={() => handleAddTag(tag)}
                      className="w-full text-left px-2 py-1 rounded hover:bg-gray-700 flex items-center"
                    >
                      <span
                        className="w-3 h-3 rounded-full mr-2"
                        style={{ backgroundColor: tag.color }}
                      />
                      <span className="text-sm text-white">{tag.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Create New Tag */}
            {!showCreateTag ? (
              <button
                onClick={() => setShowCreateTag(true)}
                className="w-full text-left px-4 py-2 text-sm text-indigo-400 hover:bg-gray-700"
              >
                <FiPlus className="inline-block w-3 h-3 mr-1" />
                Create New Tag
              </button>
            ) : (
              <div className="p-3 space-y-2">
                <input
                  type="text"
                  placeholder="Tag name"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  className="w-full px-2 py-1 bg-gray-700 text-white rounded text-sm"
                  autoFocus
                />
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={newTagColor}
                    onChange={(e) => setNewTagColor(e.target.value)}
                    className="w-8 h-8 rounded cursor-pointer"
                  />
                  <span className="text-sm text-gray-400">Choose color</span>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={handleCreateTag}
                    disabled={isCreating}
                    className="flex-1 px-2 py-1 bg-indigo-600 text-white rounded text-sm hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {isCreating ? 'Creating...' : 'Create'}
                  </button>
                  <button
                    onClick={() => {
                      setShowCreateTag(false);
                      setNewTagName('');
                    }}
                    className="flex-1 px-2 py-1 bg-gray-700 text-white rounded text-sm hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};